import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const BLUE = '#2563eb';
const BLUE_DARK = '#1d4ed8';
const BLUE_DARKER = '#1e40af';
const BLUE_LIGHT = '#f0f9ff';
const BLUE_LIGHT_2 = '#e0f2fe';
const WHITE = '#ffffff';
const TEXT = '#0f172a';
const TEXT_MUTED = '#666666';
const BORDER = '#e2e8f0';
const GREEN = '#16a34a';
const ORANGE = '#ea580c';
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function HoverBtn({ to, href, onClick, children, variant = 'solid', big, full, style }) {
  const [hover, setHover] = useState(false);

  const base = {
    fontFamily: FONT,
    fontWeight: 700,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    cursor: 'pointer',
    padding: big ? '16px 36px' : '13px 26px',
    fontSize: big ? 16 : 14,
    width: full ? '100%' : 'auto',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
    transform: hover ? 'translateY(-2px)' : 'translateY(0)',
    boxSizing: 'border-box',
  };

  const variants = {
    solid: {
      background: hover ? BLUE_DARK : BLUE,
      color: WHITE,
      border: '1px solid ' + BLUE,
      boxShadow: hover ? '0 10px 24px rgba(37,99,235,0.35)' : '0 4px 10px rgba(37,99,235,0.2)',
    },
    outline: {
      background: hover ? BLUE_LIGHT : WHITE,
      color: BLUE,
      border: '1.5px solid ' + BLUE,
      boxShadow: hover ? '0 8px 20px rgba(37,99,235,0.15)' : 'none',
    },
    onBlue: {
      background: hover ? '#f8fafc' : WHITE,
      color: BLUE_DARKER,
      border: '1px solid ' + WHITE,
      boxShadow: hover ? '0 10px 24px rgba(0,0,0,0.2)' : '0 4px 10px rgba(0,0,0,0.12)',
    },
  };

  const combined = { ...base, ...variants[variant], ...style };

  if (to) {
    return (
      <Link to={to} style={combined} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} onClick={onClick} style={combined} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {children}
    </a>
  );
}

function FlowDiagram({ isMobile }) {
  const steps = [
    { icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z', title: 'Enter your stats', body: 'MCAT, GPA, and state -- once.' },
    { icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z', title: 'Get instant matches', body: 'Every school sorted into target, match, or reach.' },
    { icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', title: 'See real distributions', body: 'Know exactly where you land at each school.' },
    { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', title: 'Track your cycle', body: 'Primaries to decisions, all in one place.' },
  ];
  return (
    <div style={{ position: 'relative', display: isMobile ? 'block' : 'flex', gap: isMobile ? 0 : 8 }}>
      {!isMobile && <div style={{ position: 'absolute', top: 27, left: '12.5%', right: '12.5%', height: 2, background: BLUE_LIGHT_2, zIndex: 0 }} />}
      {steps.map(function (s, i) {
        return (
          <div key={s.title} style={{ position: 'relative', zIndex: 1, flex: isMobile ? 'none' : 1, display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: isMobile ? 'flex-start' : 'center', textAlign: isMobile ? 'left' : 'center', gap: isMobile ? 16 : 0, marginBottom: isMobile && i < steps.length - 1 ? 28 : 0 }}>
            {isMobile && i < steps.length - 1 && <div style={{ position: 'absolute', left: 27, top: 56, bottom: -28, width: 2, background: BLUE_LIGHT_2 }} />}
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: WHITE, border: '2px solid ' + BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginBottom: isMobile ? 0 : 16 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d={s.icon} />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: TEXT, marginBottom: 6 }}>{s.title}</div>
              <div style={{ fontSize: 13, color: TEXT_MUTED, lineHeight: 1.5, maxWidth: isMobile ? 'none' : 160 }}>{s.body}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Eyebrow({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: BLUE, marginBottom: 14 }}>
      {children}
    </div>
  );
}

function WaveDivider({ fill, flip }) {
  return (
    <div style={{ lineHeight: 0, transform: flip ? 'rotate(180deg)' : 'none', marginTop: flip ? 0 : -1, marginBottom: flip ? -1 : 0 }}>
      <svg viewBox="0 0 1440 80" width="100%" height="60" preserveAspectRatio="none" style={{ display: 'block' }}>
        <path d="M0,32 C240,80 480,0 720,24 C960,48 1200,72 1440,24 L1440,80 L0,80 Z" fill={fill} />
      </svg>
    </div>
  );
}

function MatchingMockup({ isMobile }) {
  const schools = [
    { name: 'Albert Einstein College of Medicine', loc: 'Bronx, NY', mcat: 519, gpa: 3.93, pct: 82, badge: 'TARGET', color: GREEN },
    { name: 'Boston University Chobanian & Avedisian', loc: 'Boston, MA', mcat: 519, gpa: 3.92, pct: 61, badge: 'MATCH', color: BLUE },
    { name: 'Columbia Vagelos College of P&S', loc: 'New York, NY', mcat: 522, gpa: 3.97, pct: 34, badge: 'REACH', color: ORANGE },
  ];
  return (
    <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: 16, padding: isMobile ? 18 : 24, boxShadow: '0 20px 50px rgba(15,23,42,0.08)' }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {[{ l: 'MCAT', v: '524' }, { l: 'GPA', v: '3.95' }, { l: 'State', v: 'NY' }].map(function (s) {
          return (
            <div key={s.l} style={{ flex: '1 1 0', minWidth: 80, background: BLUE_LIGHT, border: '1px solid ' + BLUE_LIGHT_2, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: TEXT }}>{s.v}</div>
              <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '.05em' }}>{s.l}</div>
            </div>
          );
        })}
      </div>
      {schools.map(function (school) {
        return (
          <div key={school.name} style={{ border: '1px solid ' + BORDER, borderRadius: 12, padding: '14px 16px', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>{school.name}</div>
                <div style={{ fontSize: 11, color: TEXT_MUTED }}>{school.loc}</div>
              </div>
              <div style={{ fontSize: 10, fontWeight: 800, padding: '3px 9px', borderRadius: 5, background: school.color, color: WHITE, whiteSpace: 'nowrap' }}>{school.badge}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: school.pct + '%', height: '100%', background: school.color, borderRadius: 4 }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 800, color: school.color, width: 40, textAlign: 'right' }}>{school.pct}%</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DistributionMockup({ isMobile }) {
  const bars = [4, 8, 14, 22, 34, 48, 62, 78, 92, 100, 88, 70, 52, 36, 22, 12, 6, 3];
  const markerIndex = 13;
  return (
    <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: 16, padding: isMobile ? 18 : 26, boxShadow: '0 20px 50px rgba(15,23,42,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: TEXT }}>Columbia Vagelos College of P&amp;S</div>
        <div style={{ fontSize: 11, color: TEXT_MUTED }}>MCAT distribution</div>
      </div>
      <div style={{ position: 'relative', height: 140, display: 'flex', alignItems: 'flex-end', gap: 4, marginTop: 50, marginBottom: 10 }}>
        {bars.map(function (h, i) {
          const isMarker = i === markerIndex;
          return (
            <div key={i} style={{ position: 'relative', flex: 1, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
              <div style={{ width: '100%', height: h + '%', background: isMarker ? BLUE : BLUE_LIGHT_2, borderRadius: '3px 3px 0 0' }} />
              {isMarker && (
                <div style={{ position: 'absolute', top: -34, left: '50%', transform: 'translateX(-50%)', background: TEXT, color: WHITE, fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                  You: 524
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid ' + BORDER, paddingTop: 12 }}>
        {[{ l: 'P10', v: 517 }, { l: 'P25', v: 519 }, { l: 'Median', v: 522 }, { l: 'P75', v: 524 }, { l: 'P90', v: 526 }].map(function (p) {
          return (
            <div key={p.l} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: TEXT }}>{p.v}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase' }}>{p.l}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrackerMockup({ isMobile }) {
  const months = ['May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
  const activeIdx = 6;
  return (
    <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: 16, padding: isMobile ? 18 : 24, boxShadow: '0 20px 50px rgba(15,23,42,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, position: 'relative' }}>
        <div style={{ position: 'absolute', top: 5, left: 0, right: 0, height: 2, background: '#f1f5f9' }} />
        <div style={{ position: 'absolute', top: 5, left: 0, width: (activeIdx / (months.length - 1)) * 100 + '%', height: 2, background: BLUE }} />
        {months.map(function (m, i) {
          const done = i <= activeIdx;
          return (
            <div key={m} style={{ position: 'relative', zIndex: 1, textAlign: 'center', flex: 1 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: done ? BLUE : WHITE, border: '2px solid ' + (done ? BLUE : '#cbd5e1'), margin: '0 auto 6px' }} />
              {!isMobile && <div style={{ fontSize: 9, color: TEXT_MUTED, fontWeight: 600 }}>{m}</div>}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
        {[{ n: '10', l: 'Primaries submitted' }, { n: '6', l: 'Secondaries complete' }, { n: '2', l: 'Interviews scheduled' }].map(function (s) {
          return (
            <div key={s.l} style={{ background: BLUE_LIGHT, border: '1px solid ' + BLUE_LIGHT_2, borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: BLUE }}>{s.n}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: TEXT_MUTED, lineHeight: 1.3 }}>{s.l}</div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>Upcoming deadlines</div>
      {[
        { school: 'Boston University Chobanian', what: 'Secondary due', when: 'in 5 days', urgent: true },
        { school: 'Albert Einstein College of Medicine', what: 'Interview', when: 'in 3 weeks', urgent: false },
      ].map(function (d) {
        return (
          <div key={d.school} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: 8, background: d.urgent ? '#fff7ed' : '#f8fafc', border: '1px solid ' + (d.urgent ? '#fed7aa' : BORDER), marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: TEXT }}>{d.what} &mdash; {d.school}</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: d.urgent ? ORANGE : TEXT_MUTED, whiteSpace: 'nowrap' }}>{d.when}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function Landing() {
  const isMobile = useIsMobile();
  const px = isMobile ? 20 : 60;
  const sectionPad = isMobile ? '56px 0' : '110px 0';

  useEffect(() => {
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => { document.documentElement.style.scrollBehavior = prev; };
  }, []);

  return (
    <div style={{ background: WHITE, minHeight: '100vh', fontFamily: FONT }}>

      <PublicNav isMobile={isMobile} />

      {/* Hero */}
      <div style={{ background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE_LIGHT_2} 100%)`, padding: isMobile ? `56px ${px}px 60px` : `100px ${px}px 90px`, textAlign: 'center' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: BLUE, background: WHITE, border: '1px solid ' + BLUE_LIGHT_2, borderRadius: 20, padding: '5px 14px', display: 'inline-block', marginBottom: 24 }}>
            Free to start
          </div>
          <div style={{ fontSize: isMobile ? 34 : 58, fontWeight: 800, color: TEXT, letterSpacing: '-1.5px', lineHeight: 1.08, marginBottom: 22 }}>
            Know your odds. <span style={{ color: BLUE }}>Own your list.</span>
          </div>
          <div style={{ fontSize: isMobile ? 15 : 18, color: TEXT_MUTED, lineHeight: 1.6, marginBottom: 36, maxWidth: 560, margin: '0 auto 36px' }}>
            Stop guessing about medical school. PreVisr matches you to 150+ schools using our own proprietary matching models built on historical admissions data.
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <HoverBtn to="/signup" variant="solid" big full={isMobile}>Get started free</HoverBtn>
            <HoverBtn href="#how-it-works" variant="outline" big full={isMobile}>See how it works</HoverBtn>
          </div>
        </div>
      </div>
      <WaveDivider fill={WHITE} />

      {/* Section 1: How matching works */}
      <div id="how-it-works" style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 36 : 72, alignItems: 'center' }}>
          <div>
            <Eyebrow>How matching works</Eyebrow>
            <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              Your stats mapped against every school.
            </div>
            <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, maxWidth: 440 }}>
              Enter your MCAT, GPA, and state once. PreVisr instantly sorts every school into targets, matches, and reaches &mdash; so you know exactly where you stand before you spend a single application fee.
            </div>
          </div>
          <MatchingMockup isMobile={isMobile} />
        </div>
      </div>

      {/* Section 2: Distributions */}
      <div id="distributions" style={{ background: BLUE_LIGHT, padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 36 : 72, alignItems: 'center' }}>
          <div style={{ order: isMobile ? 2 : 1 }}>
            <DistributionMockup isMobile={isMobile} />
          </div>
          <div style={{ order: isMobile ? 1 : 2 }}>
            <Eyebrow>Built on historical data</Eyebrow>
            <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              See the distributions. Not your imagination.
            </div>
            <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, maxWidth: 440 }}>
              Our matching engine runs on our own proprietary formulas, trained on years of historical admissions data. You&rsquo;ll see exactly where your stats fall at each school &mdash; not a vague guess from a forum thread.
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Tracker */}
      <div id="tracker" style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 36 : 72, alignItems: 'center' }}>
          <div>
            <Eyebrow>Application tracker</Eyebrow>
            <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              Primaries. Secondaries. Interviews. Decisions. One place.
            </div>
            <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, maxWidth: 440 }}>
              Track everything as you go. Never miss a deadline. Never lose track of a school.
            </div>
          </div>
          <TrackerMockup isMobile={isMobile} />
        </div>
      </div>

      {/* Section 4: The flow */}
      <div id="flow" style={{ background: '#f8fafc', padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
          <Eyebrow>The PreVisr flow</Eyebrow>
          <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 16 }}>
            From your stats to your submitted list.
          </div>
          <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, maxWidth: 520, margin: '0 auto 56px' }}>
            Everything connects into one flow &mdash; from the moment you enter your stats to the day you submit your last secondary.
          </div>
          <FlowDiagram isMobile={isMobile} />
        </div>
      </div>

      <WaveDivider fill={BLUE_DARKER} />

      {/* Section 5: CTA */}
      <div id="cta" style={{ background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARKER} 100%)`, padding: isMobile ? `52px ${px}px 60px` : `90px ${px}px`, textAlign: 'center' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ fontSize: isMobile ? 26 : 40, fontWeight: 800, color: WHITE, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 16 }}>
            Ready to find your schools?
          </div>
          <div style={{ fontSize: isMobile ? 14 : 16, color: BLUE_LIGHT_2, lineHeight: 1.6, marginBottom: 32 }}>
            Build your list in minutes. Free forever. No credit card needed.
          </div>
          <HoverBtn to="/signup" variant="onBlue" big full={isMobile}>Get started free</HoverBtn>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid ' + BORDER, padding: isMobile ? `24px ${px}px` : '28px 60px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isMobile ? 14 : 0 }}>
        <div style={{ fontSize: 13, color: TEXT_MUTED }}>PreVisr &mdash; Medical school matching for pre-meds</div>
        <div style={{ display: 'flex', gap: 24 }}>
          <Link to="/guide" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Guides</Link>
          <Link to="/stats" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Stats</Link>
          <a href="#cta" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Pricing</a>
          <Link to="/privacy" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Privacy</Link>
          <Link to="/terms" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Terms</Link>
        </div>
      </div>

    </div>
  );
}
